import React from 'react';
import { Box, Paper, Typography, Button, Avatar, TextField, IconButton } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteIcon from '@mui/icons-material/Delete';

import { useAuth } from '../contexts/AuthContext';

// 時間差を計算する関数
const getTimeAgo = (dateString: string): string => {
    const now = new Date();
    const postTime = new Date(dateString);
    const diffInMs = now.getTime() - postTime.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    
    if (diffInMinutes < 1) {
        return 'たった今';
    } else if (diffInMinutes < 60) {
        return `${diffInMinutes}分前`;
    } else if (diffInHours < 24) {
        return `${diffInHours}時間前`;
    } else if (diffInDays < 7) {
        return `${diffInDays}日前`;
    } else {
        return postTime.toLocaleDateString();
    }
};

interface Hint {
    id: number;
    text: string;
    image?: string;
    latitude?: number;
    longitude?: number;
    radius?: number;
    comments?: string[];
    created_at?: string;
}

interface Comment {
    id: number;
    text: string;
    created_by_name: string;
    created_at: string;
    hint: number;
}

interface ChatHintListProps {
    hints: Hint[];
    comments: { [key: number]: Comment[] };
    commentInputs: { [key: number]: string };
    showComments: { [key: number]: boolean };
    handleCommentSend: (hintId: number) => void;
    setShowComments: React.Dispatch<React.SetStateAction<{ [key: number]: boolean }>>;
    setCommentInputs: React.Dispatch<React.SetStateAction<{ [key: number]: string }>>;
}

const ChatHintList: React.FC<ChatHintListProps> = ({
    hints,
    comments,
    commentInputs,
    showComments,
    handleCommentSend,
    setShowComments,
    setCommentInputs,
}) => {
    const { isStaff, token } = useAuth();
    const { deleteHint, deleteComment } = require('../services/api');
    // --- ここからstate管理でリロードせずに反映 ---
    const [localHints, setLocalHints] = React.useState(hints);
    const [localComments, setLocalComments] = React.useState(comments);
    React.useEffect(() => { setLocalHints(hints); }, [hints]);
    React.useEffect(() => { setLocalComments(comments); }, [comments]);
    // --- ここまで ---
    const handleDeleteHint = async (hintId: number) => {
        if (window.confirm('このヒントを削除しますか？')) {
            try {
                await deleteHint(token, hintId);
                setLocalHints((prev: Hint[]) => prev.filter(h => h.id !== hintId));
            } catch (e) {
                alert('削除に失敗しました');
            }
        }
    };
    const handleDeleteComment = async (commentId: number) => {
        if (window.confirm('このコメントを削除しますか？')) {
            try {
                await deleteComment(token, commentId);
                setLocalComments((prev: any) => {
                    const newComments = { ...prev };
                    Object.keys(newComments).forEach(hid => {
                        newComments[hid] = newComments[hid].filter((c: Comment) => c.id !== commentId);
                    });
                    return newComments;
                });
            } catch (e) {
                alert('削除に失敗しました');
            }
        }
    };
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            {localHints.map((hint, index) => (
                <Box
                    key={hint.id}
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        flexDirection: 'row',
                        gap: 2,
                        animation: 'slideInFromLeft 0.5s ease-out',
                        animationDelay: `${index * 0.1}s`,
                        animationFillMode: 'both',
                        '@keyframes slideInFromLeft': {
                            '0%': {
                                opacity: 0,
                                transform: 'translateX(-30px)'
                            },
                            '100%': {
                                opacity: 1,
                                transform: 'translateX(0)'
                            }
                        }
                    }}
                >
                    <Avatar
                        src="/image/icon.png"
                        sx={{
                            bgcolor: 'white',
                            background: 'white',
                            width: 40,
                            height: 40,
                            boxShadow: '0 4px 12px rgba(240, 98, 146, 0.18)',
                            border: '2px solid #f8bbd0'
                        }}
                    >
                        <ChatBubbleOutlineIcon sx={{ fontSize: 20, color: '#f06292' }} />
                    </Avatar>
                    <Box sx={{ width: '100%' }}>
                        <Paper
                            elevation={2}
                            sx={{
                                p: 2.5,
                                background: 'white',
                                maxWidth: '85%',
                                borderRadius: 3,
                                mb: 1.5,
                                border: '2px solid #f8bbd0',
                                boxShadow: '0 4px 20px rgba(240, 98, 146, 0.10)',
                                position: 'relative',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    left: -8,
                                    top: 16,
                                    width: 0,
                                    height: 0,
                                    borderTop: '8px solid transparent',
                                    borderBottom: '8px solid transparent',
                                    borderRight: '8px solid white',
                                },
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 25px rgba(240, 98, 146, 0.18)',
                                }
                            }}
                        >
                            {hint.created_at && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#888',
                                        fontSize: '0.75rem',
                                        display: 'block'
                                    }}
                                >
                                    {getTimeAgo(hint.created_at)}
                                </Typography>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: '#ad1457',
                                        fontSize: '1rem',
                                        lineHeight: 1.6,
                                        fontWeight: 500
                                    }}
                                >
                                    {hint.text}
                                </Typography>
                                {isStaff && (
                                    <IconButton size="small" onClick={() => handleDeleteHint(hint.id)} sx={{ ml: 1 }}>
                                        <DeleteIcon fontSize="small" color="error" />
                                    </IconButton>
                                )}
                            </Box>
                            {hint.image && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        boxShadow: '0 4px 12px rgba(240, 98, 146, 0.10)',
                                        transition: 'transform 0.3s ease',
                                        '&:hover': {
                                            transform: 'scale(1.02)'
                                        }
                                    }}
                                >
                                    <img
                                        src={hint.image}
                                        alt={`ヒント画像${hint.id}`}
                                        style={{
                                            width: '100%',
                                            height: 'auto',
                                            display: 'block'
                                        }}
                                    />
                                </Box>
                            )}
                            {/* 地図機能削除のため何も表示しない */}
                        </Paper>
                        {/* コメント＋入力欄の格納/展開ボタン */}
                        <Button
                            size="small"
                            startIcon={showComments[hint.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            onClick={() =>
                                setShowComments((prev) => ({
                                    ...prev,
                                    [hint.id]: !prev[hint.id],
                                }))
                            }
                            sx={{
                                ml: 2,
                                mb: 1.5,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2,
                                py: 0.8,
                                background: 'linear-gradient(135deg, #f06292 0%, #f8bbd0 100%)',
                                color: 'white',
                                border: 'none',
                                boxShadow: '0 2px 8px rgba(240, 98, 146, 0.18)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #d81b60 0%, #f06292 100%)',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 12px rgba(240, 98, 146, 0.22)',
                                }
                            }}
                        >
                            {showComments[hint.id]
                                ? 'コメントを隠す'
                                : `コメントを表示 (${(comments[hint.id] || []).length})`
                            }
                        </Button>
                        {/* コメントリスト＋入力欄 */}
                        {showComments[hint.id] && (
                            <Box
                                sx={{
                                    ml: 2,
                                    mb: 1,
                                    animation: 'fadeIn 0.3s ease-out',
                                    '@keyframes fadeIn': {
                                        '0%': { opacity: 0, maxHeight: 0 },
                                        '100%': { opacity: 1, maxHeight: '1000px' }
                                    }
                                }}
                            >
                                {(localComments[hint.id] || []).map((comment, commentIndex) => (
                                    <Paper
                                        key={comment.id}
                                        sx={{
                                            p: 1.5,
                                            mb: 1,
                                            background: 'white',
                                            maxWidth: '75%',
                                            borderRadius: 2,
                                            border: '2px solid #f8bbd0',
                                            boxShadow: '0 2px 8px rgba(240, 98, 146, 0.10)',
                                            animation: 'slideInComment 0.3s ease-out',
                                            animationDelay: `${commentIndex * 0.05}s`,
                                            animationFillMode: 'both',
                                            '@keyframes slideInComment': {
                                                '0%': {
                                                    opacity: 0,
                                                    transform: 'translateX(-20px)'
                                                },
                                                '100%': {
                                                    opacity: 1,
                                                    transform: 'translateX(0)'
                                                }
                                            },
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                transform: 'translateX(4px)',
                                                boxShadow: '0 4px 12px rgba(240, 98, 146, 0.15)',
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: '#ad1457',
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem'
                                                }}
                                            >
                                                {comment.created_by_name}（{getTimeAgo(comment.created_at)}）
                                            </Typography>
                                            {isStaff && (
                                                <IconButton size="small" onClick={() => handleDeleteComment(comment.id)} sx={{ ml: 1 }}>
                                                    <DeleteIcon fontSize="small" color="error" />
                                                </IconButton>
                                            )}
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#d81b60',
                                                lineHeight: 1.5
                                            }}
                                        >
                                            {comment.text}
                                        </Typography>
                                    </Paper>
                                ))}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
                                    <TextField
                                        size="small"
                                        placeholder="コメントを入力..."
                                        value={commentInputs[hint.id] || ''}
                                        onChange={(e) =>
                                            setCommentInputs((prev) => ({
                                                ...prev,
                                                [hint.id]: e.target.value,
                                            }))
                                        }
                                        sx={{
                                            flex: 1,
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                backgroundColor: 'white',
                                                boxShadow: '0 2px 8px rgba(240, 98, 146, 0.05)',
                                                border: '2px solid #f8bbd0',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    boxShadow: '0 4px 12px rgba(240, 98, 146, 0.10)',
                                                },
                                                '&.Mui-focused': {
                                                    boxShadow: '0 4px 12px rgba(240, 98, 146, 0.18)',
                                                    borderColor: '#f06292',
                                                }
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                padding: '12px 14px',
                                            }
                                        }}
                                    />
                                    <IconButton
                                        onClick={() => handleCommentSend(hint.id)}
                                        disabled={!commentInputs[hint.id]?.trim()}
                                        sx={{
                                            background: commentInputs[hint.id]?.trim()
                                                ? 'linear-gradient(135deg, #f06292 0%, #f8bbd0 100%)'
                                                : 'linear-gradient(135deg, #f8bbd0 0%, #fceabb 100%)',
                                            color: commentInputs[hint.id]?.trim() ? 'white' : '#ad1457',
                                            width: 44,
                                            height: 44,
                                            borderRadius: 2,
                                            border: '2px solid #f8bbd0',
                                            boxShadow: commentInputs[hint.id]?.trim()
                                                ? '0 2px 8px rgba(240, 98, 146, 0.18)'
                                                : '0 2px 8px rgba(248, 187, 208, 0.18)',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: commentInputs[hint.id]?.trim() ? 'translateY(-1px)' : 'none',
                                                boxShadow: commentInputs[hint.id]?.trim()
                                                    ? '0 4px 12px rgba(240, 98, 146, 0.22)'
                                                    : '0 2px 8px rgba(248, 187, 208, 0.18)',
                                            },
                                            '&:disabled': {
                                                background: 'linear-gradient(135deg, #f8bbd0 0%, #fceabb 100%)',
                                                color: '#ad1457',
                                                opacity: 0.5,
                                            }
                                        }}
                                    >
                                        <SendIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

export default ChatHintList;
